import React, { useState } from "react"
import { Modal } from "react-responsive-modal"
import { useGetDocumentsQuery, useLazyGetDocumentUrlQuery } from "services/api"
import moment from "moment"
import _ from "lodash"

function DocumentsModal({ open, onClose }) {
	const { data: documents, error, isLoading } = useGetDocumentsQuery()
	const [getDocumentUrl] = useLazyGetDocumentUrlQuery()
	const [openTabs, setOpenTabs] = useState({})

	const toggleTab = (category) => {
		setOpenTabs((prev) => ({
			...prev,
			[category]: !prev[category]
		}))
	}

	const handleDownload = async (doc) => {
		try {
			const result = await getDocumentUrl(doc.id).unwrap()
			if (result.data?.url) {
				// Create a temporary anchor element to trigger download
				const link = document.createElement("a")
				link.href = result.data.url
				link.download = result.data.filename || doc.filename
				link.target = "_blank"
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
			}
		} catch (err) {
			console.error("Error downloading document:", err)
		}
	}

	// Group documents by category
	const groupedDocuments = _.groupBy(documents?.data || [], "category")

	return (
		<Modal
			open={open}
			onClose={onClose}
			center
			classNames={{
				modal: "w-full max-w-4xl",
			}}>
			<h2 className="text-2xl font-bold mb-6 text-darkBlue">Available Documents</h2>

			{error && <div className="text-red-600 mb-4">Error loading documents. Please try again later.</div>}

			{isLoading ? (
				<div className="text-center py-8">Loading documents...</div>
			) : documents?.data?.length === 0 ? (
				<div className="text-gray-500 text-center py-8">No documents available at this time.</div>
			) : (
				<div className="space-y-2">
					{Object.entries(groupedDocuments).map(([category, docs]) => (
						<div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
							<button
								onClick={() => toggleTab(category)}
								className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors">
								<h3 className="text-lg font-semibold text-gray-700">
									{category} ({docs.length})
								</h3>
								<svg
									className={`w-5 h-5 text-gray-600 transform transition-transform ${
										openTabs[category] ? "rotate-180" : ""
									}`}
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M19 9l-7 7-7-7"
									/>
								</svg>
							</button>
							{openTabs[category] && (
								<div className="px-4 py-3 bg-white border-t border-gray-200">
									<div className="space-y-2">
										{docs.map((doc) => (
											<div
												key={doc.id}
												className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
												<div className="flex-1">
													<h4 className="font-medium text-gray-900">{doc.name}</h4>
													<p className="text-sm text-gray-500">
														{doc.filename} •{" "}
														{doc.file_size
															? `${(doc.file_size / 1024 / 1024).toFixed(2)} MB`
															: "N/A"}
													</p>
													<p className="text-xs text-gray-400">
														Uploaded {moment(doc.created_at).format("MMM D, YYYY")}
													</p>
												</div>
												<button
													onClick={() => handleDownload(doc)}
													className="ml-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm">
													<div className="flex items-center">
														<svg
															className="w-4 h-4 mr-2"
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24"
															xmlns="http://www.w3.org/2000/svg">
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																strokeWidth={2}
																d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
															/>
														</svg>
														Download
													</div>
												</button>
											</div>
										))}
									</div>
								</div>
							)}
						</div>
					))}
				</div>
			)}

			<div className="mt-6 text-right">
				<button
					onClick={onClose}
					className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors">
					Close
				</button>
			</div>
		</Modal>
	)
}

export default DocumentsModal